import { useMemo } from "react";

import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { subtitleFiltersWithCustomColors, useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { default as DurationService } from "app/services/DurationService";
import { useTranslation } from "pro-features/components/WithTranslation";

import { getColumnConfig, getComparisonColumnConfig, stripedBackground } from "./getColumnConfig";
import { useTopPlayersComparisonData } from "./useTopPlayersComparisonData";

export const TopPlayersComparisonWidget = () => {
    const t = useTranslation();
    const periodOverPeriodCompareType = DurationService.getPeriodOverPeriodCompareType();
    const subtitleFilters = useSubtitleFilters("date");
    const enrichedSubtitleFilters = subtitleFiltersWithCustomColors(
        subtitleFilters,
        colorsPalettes.carbon[200],
        stripedBackground,
    );
    const { categories } = useCategoriesComparison();
    const { data, isLoading, isError } = useTopPlayersComparisonData(categories);

    const columnConfig = useMemo(
        () =>
            periodOverPeriodCompareType
                ? getComparisonColumnConfig(categories, periodOverPeriodCompareType)
                : getColumnConfig(categories),
        [categories, periodOverPeriodCompareType],
    );

    return (
        <WidgetContainer
            withBorder
            minHeight={385}
            noData={isError}
            title={t("industry.research.market.performance.players.title")}
            subtitleFilters={periodOverPeriodCompareType ? enrichedSubtitleFilters : subtitleFilters}
            automationId="industry-analysis-overview-comparison-top-players"
        >
            <Table
                theme="white"
                sticky={false}
                data={data}
                columnConfig={columnConfig}
                loaderRowCount={5}
                isLoading={isLoading}
            />
            {!isLoading ? (
                <Flex
                    justifyContent="flex-end"
                    padding="8px 16px"
                >
                    <Button
                        isDisabled
                        type="flat"
                    >
                        {t("industry.research.market.performance.players.cta")}
                    </Button>
                </Flex>
            ) : null}
        </WidgetContainer>
    );
};
